// usePresignedDownload() → { download(jobId, artifact), pending }     (IMPL_ADDENDUM §2c)
// Downloads go straight to S3 when the backend can presign: GET …/artifacts/{sha}/presigned,
// a one-byte probe of that URL (stores that predate S3 answer 404 there), then a navigation to
// it. Any failure (503 store not S3-backed, probe error, timeout) falls back to the same-origin
// /v1/jobs/{id}/artifacts/{sha}, which the backend serves as an attachment with the file name.
// JSON the UI renders never comes from here (useArtifactJson stays same-origin).
// `pending` is the sha256 being prepared (show "Preparing…" on that row), else null.
//   downloadArtifact(jobId, artifact) → Promise<"presigned" | "same-origin">   (no hook needed)
import { useCallback, useState } from "react";
import { api } from "@/lib/api";
import type { Artifact } from "@/types";

export const PRESIGN_EXPIRES_S = 600;
const PROBE_TIMEOUT_MS = 4000;

type Downloadable = Pick<Artifact, "sha256"> & Partial<Pick<Artifact, "name">>;

async function reachable(url: string): Promise<boolean> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(url, { headers: { Range: "bytes=0-0" }, cache: "no-store", credentials: "omit", signal: ctl.signal });
    void res.body?.cancel().catch(() => undefined);
    return res.ok;
  } catch {
    return false;
  } finally {
    clearTimeout(t);
  }
}

function navigate(href: string, filename?: string) {
  const a = document.createElement("a");
  a.href = href;
  if (filename) a.download = filename; // honoured same-origin only
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

async function downloadArtifact(jobId: string, artifact: Downloadable): Promise<"presigned" | "same-origin"> {
  try {
    const { url } = await api.presigned(jobId, artifact.sha256, PRESIGN_EXPIRES_S);
    if (url && (await reachable(url))) {
      navigate(url);
      return "presigned";
    }
  } catch {
    /* fall back to same-origin */
  }
  navigate(api.artifactURL(jobId, artifact.sha256), artifact.name);
  return "same-origin";
}

export function usePresignedDownload() {
  const [pending, setPending] = useState<string | null>(null);
  const download = useCallback(async (jobId: string, artifact: Downloadable) => {
    setPending(artifact.sha256);
    try {
      return await downloadArtifact(jobId, artifact);
    } finally {
      setPending((p) => (p === artifact.sha256 ? null : p));
    }
  }, []);
  return { download, pending };
}
